export const includesWith = (array, target, comparator) => {
  for (const value of array) {
    if (comparator(target, value)) {
      return true
    }
  }
  return false
}

export const unique = array => [...new Set(array)]

export const uniqueWith = (array, comparator) => {
  const result = []

  for (const current of array) {
    if (!includesWith(result, current, comparator)) {
      result.push(current)
    }
  }

  return result
}

export const flatten = array => array.reduce(
  (flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten),
  []
)

export const flatMap = (array, mapFn) => flatten(array.map(mapFn))

export const toObj = (array, keySelector, eleSelector = null) => array.reduce(
  (obj, ele) => ({
    ...obj,
    [keySelector(ele)]: eleSelector != null ? eleSelector(ele) : ele,
  }),
  {}
)

export const allSame = (array, comparator = null) => {
  for (let i = 1; i < array.length; i++) {
    const match = comparator ? comparator(array[i], array[0]) : array[i] === array[0]

    if (!match) {
      return false
    }
  }

  return true
}

export const zip = arrays => (
  arrays[0].length > 0
    ? arrays[0].map((_, i) => arrays.map(array => array[i]))
    : []
)

export const zipWith = (arrays, action) => zip(arrays).map(vals => action(...vals))

export const chunk = (array, chunkSize) => {
  const chunks = []

  for (let i = 0; i < array.length; i += chunkSize) {
    const newChunk = array.slice(i, i + chunkSize)
    chunks.push(newChunk)
  }

  return chunks
}

export const arrayMin = (array, getCompareValue, getReturnValue) => {
  if (!getReturnValue) {
    // If getReturnValue is not specified, default to using getCompareValue
    getReturnValue = getCompareValue
  }
  const minElem = array.reduce((minSoFar, elem) => {
    if (!minSoFar) {
      return elem
    }
    const m1 = getCompareValue(minSoFar)
    const m2 = getCompareValue(elem)
    return m1 < m2 ? minSoFar : elem
  }, null)

  return getReturnValue(minElem)
}

export const arrayMax = (array, getCompareValue, getReturnValue) => {
  if (!getReturnValue) {
    // If getReturnValue is not specified, default to using getCompareValue
    getReturnValue = getCompareValue
  }
  const maxElem = array.reduce((maxSoFar, elem) => {
    if (!maxSoFar) {
      return elem
    }
    const m1 = getCompareValue(maxSoFar)
    const m2 = getCompareValue(elem)
    return m1 > m2 ? maxSoFar : elem
  }, null)

  return getReturnValue(maxElem)
}

export const contains = (array, compareFn) => {
  for (const elem of array) {
    if (compareFn(elem)) {
      return true
    }
  }
  return false
}

export const indexOf = (array, compareFn) => {
  for (let i = 0; i < array.length; i++) {
    if (compareFn(array[i])) {
      return i
    }
  }
  return -1
}

export const intersect = (array1, array2, compareFn = ((a, b) => a === b)) => {
  const result = []
  for (const elem of array1) {
    if (contains(array2, e => compareFn(elem, e))) {
      result.push(elem)
    }
  }
  return uniqueWith(result, compareFn)
}

/*
 * Good for use in redux reducers as they don't mutate the input array
 * However, they probably aren't the most efficient way of doing things if you do not need this
 */

export const insertItemAt = (array, item, index) => {
  const newArray = array.slice()
  newArray.splice(index, 0, item)
  return newArray
}

export const removeItemAt = (array, index) => {
  const newArray = array.slice()
  newArray.splice(index, 1)
  return newArray
}

export const updateItemAt = (array, newItem, atIndex) =>
  array.map((item, index) => (index === atIndex ? newItem : item))
